import React, { useState, useEffect } from 'react';
import { Save, Phone, Mail, MapPin, Youtube, Instagram, Share2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ImageUpload } from './ImageUpload';
import {
  DEFAULT_SOCIAL_LINKS,
  fetchSocialLinksFromDB,
  saveSocialLinksToDB,
  normalizeSocialUrl,
} from '../../hooks/useSocialLinks';

interface ProfileFormData {
  name: string;
  bio: string;
  contact: {
    phone: string;
    email: string;
    address: string;
    youtube: string;
    tiktok: string;
    instagram: string;
  };
  profile_image_url: string;
}

export const ProfileManagement: React.FC = () => {
  const [profile, setProfile] = useState<ProfileFormData>({
    name: '',
    bio: '',
    contact: {
      phone: '',
      email: '',
      address: '',
      youtube: DEFAULT_SOCIAL_LINKS.youtube,
      tiktok: DEFAULT_SOCIAL_LINKS.tiktok,
      instagram: DEFAULT_SOCIAL_LINKS.instagram,
    },
    profile_image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const [{ data, error: fetchErr }, socialData] = await Promise.all([
        supabase.from('profiles').select('*').limit(1).maybeSingle(),
        fetchSocialLinksFromDB(),
      ]);

      if (fetchErr && fetchErr.code !== 'PGRST116') {
        throw fetchErr;
      }

      if (data) {
        setProfileId(data.id);
        setProfile({
          name: data.name || '',
          bio: data.bio || '',
          contact: {
            phone: data.contact?.phone || '',
            email: data.contact?.email || '',
            address: data.contact?.address || '',
            youtube: data.contact?.youtube || socialData.youtube || DEFAULT_SOCIAL_LINKS.youtube,
            tiktok: data.contact?.tiktok || socialData.tiktok || DEFAULT_SOCIAL_LINKS.tiktok,
            instagram:
              data.contact?.instagram || socialData.instagram || DEFAULT_SOCIAL_LINKS.instagram,
          },
          profile_image_url: data.profile_image_url || ''
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Gagal memuat data profil');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.startsWith('contact.')) {
      const contactField = field.split('.')[1];
      setProfile(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          [contactField]: value
        }
      }));
    } else {
      setProfile(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const normalizedContact = {
        ...profile.contact,
        youtube: normalizeSocialUrl('youtube', profile.contact.youtube),
        tiktok: normalizeSocialUrl('tiktok', profile.contact.tiktok),
        instagram: normalizeSocialUrl('instagram', profile.contact.instagram),
      };

      const profileData = {
        name: profile.name,
        bio: profile.bio,
        contact: normalizedContact,
        profile_image_url: profile.profile_image_url
      };

      if (profileId) {
        const { error: updateErr } = await supabase
          .from('profiles')
          .update(profileData)
          .eq('id', profileId);

        if (updateErr) throw updateErr;
      } else {
        const { data, error: insertErr } = await supabase
          .from('profiles')
          .insert([profileData])
          .select()
          .single();

        if (insertErr) throw insertErr;
        setProfileId(data.id);
      }

      // Sinkronkan juga link sosial media ke contact_info & localStorage
      const savedLinks = await saveSocialLinksToDB({
        youtube: normalizedContact.youtube,
        tiktok: normalizedContact.tiktok,
        instagram: normalizedContact.instagram,
      });

      setProfile(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          ...savedLinks,
        }
      }));

      setSuccess('Profil & tautan sosial media berhasil disimpan!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Gagal menyimpan profil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Memuat data profil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Kelola Profil & Sosial Media</h3>
          <p className="mt-1 text-sm text-gray-500">
            Edit informasi profil dan tautan sosial media (YouTube, TikTok, Instagram) yang ditampilkan di portofolio
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="text-sm text-green-700">{success}</div>
            </div>
          )}

          {/* Profile Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto Profil
            </label>
            <ImageUpload
              value={profile.profile_image_url}
              onChange={(url) => setProfile(prev => ({ ...prev, profile_image_url: url }))}
              bucket="portfolio-images"
              folder="profile"
              accept="image/*"
              maxSize={5}
              className="w-full"
            />
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Nama Lengkap *
            </label>
            <input
              type="text"
              id="name"
              required
              value={profile.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Masukkan nama lengkap"
            />
          </div>

          {/* Bio */}
          <div>
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
              Bio/Deskripsi
            </label>
            <textarea
              id="bio"
              rows={4}
              value={profile.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ceritakan tentang diri Anda..."
            />
          </div>

          {/* Social Media Section */}
          <div className="space-y-4 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-blue-600" />
              <h4 className="text-md font-semibold text-gray-900">
                Pengaturan Sosial Media (YouTube, TikTok, Instagram)
              </h4>
            </div>
            <p className="text-xs text-gray-500">
              Masukkan URL lengkap atau cukup username (contoh: <code>@evano.rama</code>)
            </p>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label htmlFor="youtube" className="block text-sm font-medium text-gray-700 mb-2">
                  <Youtube className="inline h-4 w-4 mr-1 text-red-600" />
                  YouTube Vano
                </label>
                <input
                  type="text"
                  id="youtube"
                  value={profile.contact.youtube}
                  onChange={(e) => handleInputChange('contact.youtube', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="https://www.youtube.com/@evano.rama"
                />
              </div>

              <div>
                <label htmlFor="tiktok" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block font-bold mr-1">♪</span>
                  TikTok Vano
                </label>
                <input
                  type="text"
                  id="tiktok"
                  value={profile.contact.tiktok}
                  onChange={(e) => handleInputChange('contact.tiktok', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="https://www.tiktok.com/@evano.rama"
                />
              </div>

              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                  <Instagram className="inline h-4 w-4 mr-1 text-pink-600" />
                  Instagram Vano
                </label>
                <input
                  type="text"
                  id="instagram"
                  value={profile.contact.instagram}
                  onChange={(e) => handleInputChange('contact.instagram', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="https://instagram.com/evano.rama"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4 pt-2 border-t border-gray-100">
            <h4 className="text-md font-medium text-gray-900">Informasi Kontak</h4>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Nomor Telepon
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={profile.contact.phone}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="08xxxxxxxxxx"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="inline h-4 w-4 mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={profile.contact.email}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline h-4 w-4 mr-1" />
                Alamat
              </label>
              <textarea
                id="address"
                rows={2}
                value={profile.contact.address}
                onChange={(e) => handleInputChange('contact.address', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Alamat lengkap"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0">
            <button
              type="submit"
              disabled={saving || !profile.name}
              className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Simpan Profil
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
